import { API } from '../api'
import {
  getServiceNameFromUrl,
  getSecureKey,
  isAuth0ProviderSecureKey,
} from './helperFunctions'
import {
  canvasAuthService,
  getCanvasAccessTokenFromStorage,
} from '../services/CanvasAuthService'
import { reportFrontendError } from '../telemetry/rum'

const JWTRegex = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/

export interface SSEEvent {
  type: string
  data?: any
  session_id?: string
  content?: string
  message?: string
  ctas?: any
  timestamp?: string
}

export interface SSEConfig {
  url: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: any
  headers?: Record<string, string>
}

export interface SSEHandlers {
  onStart?: (event: SSEEvent) => void
  onContent?: (event: SSEEvent) => void
  onEnd?: (event: SSEEvent) => void
  onError?: (event: SSEEvent) => void
  onData?: (event: SSEEvent) => void
  onMessage?: (event: SSEEvent) => void
  onCustom?: (eventType: string, event: SSEEvent) => void
}

export interface SSEOptions {
  timeout?: number
  retryAttempts?: number
  retryDelay?: number
}

export interface SSEConnection {
  cancel: () => void
  promise: Promise<void>
}

export const createSSEConnection = (
  config: SSEConfig,
  handlers: SSEHandlers = {},
  options: SSEOptions = {}
): SSEConnection => {
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null
  let timeoutId: NodeJS.Timeout | null = null
  let abortController: AbortController | null = null
  let isCancelled = false

  const cancel = () => {
    isCancelled = true
    if (abortController) {
      try {
        abortController.abort()
      } catch (e) {
        console.warn('Error aborting fetch:', e)
      }
      abortController = null
    }
    if (reader) {
      try {
        reader.cancel()
      } catch (e) {
        console.warn('Error cancelling reader:', e)
      }
      reader = null
    }
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  const connectionPromise = (async (): Promise<void> => {
    const { timeout = 30000, retryAttempts = 2, retryDelay = 1000 } = options

    const { url, method = 'POST', body, headers: customHeaders = {} } = config

    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      ...customHeaders,
    }

    const serviceName = getServiceNameFromUrl(url)
    const secureKey = getSecureKey()

    // Salesforce Canvas: the fetch-based SSE stream bypasses the axios `API`
    // interceptor, so mirror its canvas-token logic here. Prefer the token the
    // host persists to localStorage; fall back to the in-memory singleton.
    const canvasJWT =
      getCanvasAccessTokenFromStorage() || canvasAuthService.getAccessToken()

    if (secureKey && new RegExp(JWTRegex).test(secureKey)) {
      if (isAuth0ProviderSecureKey(secureKey)) {
        headers['Authorization'] = `Bearer ${secureKey}`
      } else if (serviceName) {
        headers['Internal-Authorization'] = `Bearer-${serviceName} ${secureKey}`
      }
    } else if (canvasJWT) {
      const useAuth0 = import.meta.env.VITE_SALESFORCE_USE_AUTH0 === 'true'
      if (useAuth0) {
        headers['Authorization'] = `Bearer ${canvasJWT}`
      } else if (serviceName) {
        headers['Internal-Authorization'] = `Bearer-${serviceName} ${canvasJWT}`
      }
    } else {
      const token = localStorage.getItem('auth0-access-token')
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }

    const baseURL = API.defaults.baseURL || ''
    const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`

    let attempt = 0
    let lastError: Error | null = null

    while (attempt < retryAttempts && !isCancelled) {
      try {
        abortController = new AbortController()

        const requestOptions: RequestInit = {
          method,
          headers,
          signal: abortController.signal,
          ...(body && { body: JSON.stringify(body) }),
        }

        const response = await fetch(fullUrl, requestOptions)

        if (isCancelled) {
          return
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        reader = response.body?.getReader() || null
        if (!reader) {
          throw new Error('No response body reader available')
        }

        if (isCancelled) {
          reader.cancel()
          reader = null
          return
        }

        const decoder = new TextDecoder()
        let buffer = ''
        let isTimeout = false
        let currentEventType: string | null = null

        try {
          while (true && !isCancelled) {
            const readPromise = reader.read()

            // Start a fresh timeout for this read iteration
            const perReadTimeoutPromise = new Promise<never>((_, reject) => {
              timeoutId = setTimeout(() => {
                isTimeout = true
                if (reader) {
                  reader.cancel()
                }
                reject(new Error('SSE connection timeout'))
              }, timeout)
            })

            let result: ReadableStreamReadResult<Uint8Array>
            try {
              result = await Promise.race([readPromise, perReadTimeoutPromise])
            } catch (error) {
              if (isCancelled) {
                return
              }
              throw error
            } finally {
              if (timeoutId) {
                clearTimeout(timeoutId)
                timeoutId = null
              }
            }

            const { done, value } = result

            if (done || isCancelled) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
              if (line.trim() === '' || isCancelled) continue

              // Handle event: lines (SSE event type)
              if (line.startsWith('event: ')) {
                currentEventType = line.slice(7).trim()
                continue
              }

              // Handle data: lines
              if (line.startsWith('data: ')) {
                try {
                  const rawData = JSON.parse(line.slice(6))
                  const normalizedEvent = normalizeSSEEvent(
                    rawData,
                    currentEventType
                  )
                  await handleSSEEvent(normalizedEvent, handlers)
                  // Reset event type after processing (SSE spec: each event resets)
                  currentEventType = null
                } catch (parseError) {
                  console.warn(
                    'Failed to parse SSE data:',
                    parseError,
                    'Line:',
                    line
                  )
                }
              }
            }
          }

          if (timeoutId) {
            clearTimeout(timeoutId)
            timeoutId = null
          }
        } finally {
          if (timeoutId) {
            clearTimeout(timeoutId)
            timeoutId = null
          }
          if (!isTimeout && !isCancelled && reader) {
            reader.releaseLock()
          }
        }

        if (isCancelled) {
          return
        }

        return
      } catch (error: any) {
        if (isCancelled) {
          return
        }

        // Don't treat AbortError as a real error if we cancelled
        if (error?.name === 'AbortError' && isCancelled) {
          return
        }

        lastError = error as Error
        attempt++

        if (attempt < retryAttempts && !isCancelled) {
          console.warn(
            `SSE connection attempt ${attempt} failed, retrying in ${retryDelay}ms:`,
            error
          )
          await new Promise(resolve => setTimeout(resolve, retryDelay))
        }
      } finally {
        // Don't clear abortController here - it might still be needed for cancellation
        // It will be cleared when cancel() is called or when connection completes
      }
    }

    if (isCancelled) {
      return
    }

    const sseError =
      lastError || new Error('SSE connection failed after all retry attempts')
    reportFrontendError('sse.error', sseError, {
      'sse.url': fullUrl,
      'sse.method': method,
      'sse.attempts': attempt,
      'http.request_body': body ? JSON.stringify(body) : '',
    })
    throw sseError
  })()

  return {
    cancel,
    promise: connectionPromise,
  }
}

/**
 * Normalizes SSE events from different formats to a common structure
 * Handles both:
 * 1. Old format: { type: "start", content: "...", session_id: "..." }
 * 2. New JSON-RPC format: { jsonrpc: "2.0", method: "notifications/progress", params: { eventType: "...", sessionId: "...", contentBlock: {...} } }
 */
const normalizeSSEEvent = (
  rawData: any,
  eventTypeFromHeader: string | null
): SSEEvent => {
  // Check if this is JSON-RPC 2.0 format
  if (rawData.jsonrpc === '2.0' && rawData.params) {
    const params = rawData.params
    const eventType = params.eventType || eventTypeFromHeader || 'unknown'

    // Extract content from contentBlock if present
    let content = ''
    if (params.contentBlock?.content?.text) {
      content = params.contentBlock.content.text
    } else if (params.contentBlock?.content) {
      // Handle case where content might be a string directly
      content =
        typeof params.contentBlock.content === 'string'
          ? params.contentBlock.content
          : ''
    }

    return {
      type: eventType,
      session_id: params.sessionId || rawData.sessionId,
      content: content,
      timestamp: params.timestamp || rawData.timestamp,
      data: rawData, // Keep original data for custom handlers
    }
  }

  // Old format or direct format
  // If we have an event type from the header, use it; otherwise use type from data
  const eventType =
    eventTypeFromHeader || rawData.type || rawData.eventType || 'unknown'

  return {
    type: eventType,
    session_id: rawData.session_id || rawData.sessionId,
    content: rawData.content || '',
    message: rawData.message,
    ctas: rawData.ctas,
    timestamp: rawData.timestamp,
    data: rawData,
  }
}

const handleSSEEvent = async (
  eventData: SSEEvent,
  handlers: SSEHandlers
): Promise<void> => {
  const { type } = eventData

  // Map new event types to existing handlers
  let mappedType = type
  if (type === 'stream_start' || type === 'assistant-response.start') {
    mappedType = 'start'
  } else if (
    type === 'assistant-response.delta' ||
    type === 'assistant-response.content'
  ) {
    mappedType = 'content'
  } else if (
    type === 'assistant-response.completed' ||
    type === 'done' ||
    type === 'stream_end'
  ) {
    mappedType = 'end'
  }

  // Always call onCustom with the original event type for custom handling
  if (type && type !== mappedType) {
    handlers.onCustom?.(type, eventData)
  }

  switch (mappedType) {
    case 'start':
      handlers.onStart?.(eventData)
      break
    case 'content':
      handlers.onContent?.(eventData)
      break
    case 'end':
      handlers.onEnd?.(eventData)
      break
    case 'error':
      handlers.onError?.(eventData)
      break
    case 'message':
      handlers.onMessage?.(eventData)
      break
    default:
      handlers.onCustom?.(type, eventData)
      handlers.onData?.(eventData)
  }
}

/**
 * Convenience function for creating SSE connections with common patterns
 */
export const createSSEConnectionWithDefaults = (
  url: string,
  body: any,
  handlers: SSEHandlers,
  options?: SSEOptions
): SSEConnection => {
  return createSSEConnection({ url, body }, handlers, options)
}

/**
 * Helper for creating SSE connections with typing simulation
 */
export const createTypingSSEConnection = (
  config: SSEConfig,
  handlers: SSEHandlers & {
    onTyping?: (text: string) => void
    onTypingComplete?: (finalText: string) => void
  },
  options?: SSEOptions & {
    typingDelay?: number
  }
): SSEConnection => {
  const { typingDelay = 100 } = options || {}
  let typingTimeout: NodeJS.Timeout | null = null
  let accumulatedText = ''
  let isNewResponse = false

  const enhancedHandlers: SSEHandlers = {
    ...handlers,
    onStart: event => {
      const eventType = event.data?.params?.eventType || event.type || ''
      if (eventType === 'assistant-response.start' || eventType === 'start') {
        accumulatedText = ''
        isNewResponse = true
      }
      handlers.onStart?.(event)
    },
    onContent: event => {
      const content = event.content || ''

      if (isNewResponse) {
        accumulatedText = ''
        isNewResponse = false
      }

      accumulatedText += content

      if (typingTimeout) {
        clearTimeout(typingTimeout)
      }

      typingTimeout = setTimeout(() => {
        handlers.onTyping?.(accumulatedText)
      }, typingDelay)

      handlers.onContent?.(event)
    },
    onEnd: event => {
      if (typingTimeout) {
        clearTimeout(typingTimeout)
        typingTimeout = null
      }

      let finalText = accumulatedText
      if (event.data?.params?.contentBlock?.content?.text) {
        finalText = event.data.params.contentBlock.content.text
        accumulatedText = finalText
      }

      handlers.onTypingComplete?.(finalText)
      handlers.onEnd?.(event)
    },
    onError: event => {
      if (typingTimeout) {
        clearTimeout(typingTimeout)
        typingTimeout = null
      }
      handlers.onError?.(event)
    },
    onCustom: (eventType, event) => {
      if (eventType === 'assistant-response.start') {
        accumulatedText = ''
        isNewResponse = false
      }
      handlers.onCustom?.(eventType, event)
    },
  }

  const connection = createSSEConnection(config, enhancedHandlers, options)

  const originalCancel = connection.cancel
  connection.cancel = () => {
    if (typingTimeout) {
      clearTimeout(typingTimeout)
      typingTimeout = null
    }
    originalCancel()
  }

  return connection
}
